import { useMemo, useState, useSyncExternalStore } from "react";
import { WebNotifications } from "../notifications";
import { useApp } from "../ui";
import { Tile, YouIcon } from "./YouParts";

const clock = (minutes: number): string => `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;

/** Phone alerts, set once from Settings. Off until the person turns them on; the browser's permission prompt needs a tap. */
export function AlertsSetting() {
  const { notifications } = useApp();
  const state = useSyncExternalStore(notifications.subscribe, notifications.getState);
  const details = useSyncExternalStore(notifications.subscribe, notifications.getDetails);
  const finished = useSyncExternalStore(notifications.subscribe, notifications.getFinished);
  const quietRaw = useSyncExternalStore(notifications.subscribe, notifications.getQuietRaw);
  const quiet = useMemo(() => notifications.getQuiet(), [notifications, quietRaw]);
  const setTimeOf = (which: "from" | "to", value: string) => {
    const m = /^(\d{1,2}):(\d{2})$/.exec(value);
    if (!m || !quiet) return;
    notifications.setQuiet({ ...quiet, [which]: Math.min(23, Number(m[1])) * 60 + Math.min(59, Number(m[2])) });
  };
  const [error, setError] = useState<string | null>(null);

  const turnOn = async () => {
    setError(null);
    if ((await notifications.enable()) === "denied") setError("Your browser blocked notifications for this app.");
  };

  const note =
    state === "on"
      ? "On. You'll hear when an agent needs you or finishes."
      : state === "denied"
        ? "Blocked in your browser settings for this app."
        : state === "unsupported"
          ? "Add the app to your Home Screen to get notifications."
          : "Know when an agent needs you or finishes.";

  return (
    <section aria-labelledby="alerts-title" data-testid="alerts-setting">
      <div className="group-label" id="alerts-title">Alerts</div>
      <div className="card you-list">
        <div className="you-row static">
          <Tile>{YouIcon.bell}</Tile>
          <span className="you-row-main">
            <strong>Phone alerts</strong>
            <span>{note}</span>
            {error && <span className="alerts-err">{error}</span>}
          </span>
          {(state === "on" || state === "off") && (
            <button className="btn sm secondary you-pill" onClick={() => (state === "on" ? notifications.disable() : void turnOn())}>
              {state === "on" ? "Turn off" : "Turn on"}
            </button>
          )}
        </div>
        {state === "on" && (
          <div className="you-row static">
            <Tile>{YouIcon.bell}</Tile>
            <span className="you-row-main">
              <strong>When an agent finishes</strong>
              <span>{finished ? "On. You hear when a task is done." : "Off. You only hear when an agent needs you."}</span>
            </span>
            <button className="btn sm secondary you-pill" data-testid="alerts-finished" onClick={() => notifications.setFinished(!finished)}>
              {finished ? "Turn off" : "Turn on"}
            </button>
          </div>
        )}
        {state === "on" && (
          <div className="you-row static quiet-row">
            <Tile>{YouIcon.moon}</Tile>
            <span className="you-row-main">
              <strong>Quiet hours</strong>
              <span>{quiet ? "On. Only approvals and questions alert in these hours." : "Off. Pick hours when only approvals and questions should alert."}</span>
              {quiet && (
                <span className="quiet-times">
                  <label>
                    From
                    <input type="time" className="input" value={clock(quiet.from)} aria-label="Quiet hours start" onChange={(e) => setTimeOf("from", e.target.value)} />
                  </label>
                  <label>
                    To
                    <input type="time" className="input" value={clock(quiet.to)} aria-label="Quiet hours end" onChange={(e) => setTimeOf("to", e.target.value)} />
                  </label>
                </span>
              )}
            </span>
            <button className="btn sm secondary you-pill" data-testid="alerts-quiet" onClick={() => notifications.setQuiet(quiet ? null : WebNotifications.defaultQuiet)}>
              {quiet ? "Turn off" : "Turn on"}
            </button>
          </div>
        )}
        {state === "on" && (
          <div className="you-row static">
            <Tile>{YouIcon.bell}</Tile>
            <span className="you-row-main">
              <strong>Show session details</strong>
              <span>
                {details
                  ? "On. Alerts say which agent, folder and chat, and approvals get Approve and Deny buttons where your phone allows. They travel encrypted, so Apple and Google can't read them, and commands are never included."
                  : "Off. Alerts use fixed wording, so nothing about a session leaves your computer."}
              </span>
            </span>
            <button className="btn sm secondary you-pill" data-testid="alerts-details" onClick={() => notifications.setDetails(!details)}>
              {details ? "Turn off" : "Turn on"}
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
